export type ActionPayload = {
  data: { message: string };
  error?: string;
  status: number | 'FETCH_ERROR' | 'PARSING_ERROR' | 'CUSTOM_ERROR';
};

/**
 * Codes the backend idempotency guard bounces a submit with. It sends them as
 * `{ message, errors: [{ message: code }] }` where `message` describes the guard mechanism itself
 * ("This idempotency key does not match the current session."), so it must never be toasted. The global
 * handler skips these and the calling component supplies wording an operator can act on.
 */
export const IDEMPOTENCY_GUARD_CODE = {
  /** 409: an identical submit is already in flight. */
  DUPLICATE: 'duplicate_request',
  /** 409: the key was claimed under a different session scope, so its response cannot be replayed. */
  SCOPE_CONFLICT: 'idempotency_key_conflict',
  /** 503: the guard could not reach its store and refused to let the submit through unchecked. */
  UNAVAILABLE: 'idempotency_unavailable'
} as const;

export type IdempotencyGuardCode =
  (typeof IDEMPOTENCY_GUARD_CODE)[keyof typeof IDEMPOTENCY_GUARD_CODE];

const IDEMPOTENCY_GUARD_CODES: string[] = Object.values(IDEMPOTENCY_GUARD_CODE);

export const getIdempotencyGuardCode = (payload: unknown): IdempotencyGuardCode | null => {
  const errors = (payload as { data?: { errors?: { message?: string }[] } })?.data?.errors;
  const code = errors?.find(
    ({ message }) => !!message && IDEMPOTENCY_GUARD_CODES.includes(message)
  )?.message;
  return (code as IdempotencyGuardCode) ?? null;
};

export const getErrorLevel = (
  actionPayload: ActionPayload,
  isCriticalEndpoint: boolean
): 'info' | 'warn' | 'error' | 'critical' => {
  let level: 'info' | 'warn' | 'error' | 'critical' = 'error';

  const errorStatus = 'status' in actionPayload && actionPayload.status;

  if (errorStatus) {
    if (errorStatus === 'FETCH_ERROR' && actionPayload.error === 'AbortError') {
      level = 'info';
    }
    if ((errorStatus === 'FETCH_ERROR' && !isCriticalEndpoint) || errorStatus === 'PARSING_ERROR') {
      level = 'warn';
    }
  }

  return level;
};

export const getErrorMessage = (actionPayload: ActionPayload, errorMessage: string) => {
  const errorStatus = actionPayload.status;
  if (actionPayload.status === 'FETCH_ERROR' && actionPayload.error === 'AbortError') {
    return 'AbortError';
  }
  if ('status' in actionPayload && errorStatus === 'FETCH_ERROR') {
    return 'Fetch error';
  }
  if (errorMessage) {
    return errorMessage;
  }
  return 'Uncaught Error';
};
